// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { FeaturesPerUserResp } from '@evolvemep/api-ts';
import type { AgChartOptions, AgSeriesTooltipRendererParams } from 'ag-charts-enterprise';

import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import { useAnalyticsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsChart';

export const FeaturesPerUserChart = ({ showDemo, ...props }: AnalyticsChartProps<FeaturesPerUserResp>) => {
  const [data, loading] = useAnalyticsChart(`usage-stats/v1/reporting/summary/features`, props);

  const options = useMemo<AgChartOptions>(
    () => ({
      data: data?.featuresPerUser
        .toSorted((a, b) => b.totalFeatures - a.totalFeatures)
        .map((u) => ({
          ...u,
          name: u.email.split('@')[0],
        })),
      title: {
        enabled: !showDemo,
        text: 'Features per User',
        textAlign: 'left',
      },
      series: [
        {
          type: 'bar',
          xKey: 'name',
          yKey: 'totalFeatures',
          yName: 'Total Features',
          fill: 'orange',
          tooltip: {
            renderer: ({ datum }: AgSeriesTooltipRendererParams<FeaturesPerUserResp['featuresPerUser'][number]>) => ({
              heading: datum.email,
              data: [
                {
                  label: 'Total Features',
                  value: String(datum.totalFeatures),
                },
              ],
            }),
          },
        },
      ],
      axes: {
        x: {
          type: 'category',
          position: 'bottom',
          label: {
            rotation: -35,
            minSpacing: 0,
          },
        },
        y: {
          type: 'number',
          position: 'left',
          label: {},
        },
      },
    }),
    [data, showDemo],
  );

  return <ChartRenderer options={options} loading={loading} />;
};
